/**
 * Stats360Page — Hospital 360° Statistics / Comparison dashboard.
 *
 * Route: /stats360  (perm: dashboard.statistics_360)
 */
import { useEffect, useState } from 'react';
import { finance360API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';
import {
  GitCompareArrows, TrendingUp, TrendingDown, IndianRupee, Users, Stethoscope,
  HeartPulse, Wallet, Bed, BedDouble, Activity,
} from 'lucide-react';

const fmtINR = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const PRESET_LABELS = {
  morning_today_vs_yesterday: 'Morning today vs yesterday',
  today_so_far_vs_yesterday_same_time: 'Today so far vs yesterday same time',
  this_week_vs_last_week: 'This week vs last week',
  this_month_vs_last_month: 'This month vs last month',
  this_year_vs_last_year: 'This year vs last year',
  custom: 'Custom date range',
};

const METRIC_GROUPS = [
  {
    title: 'Financial Performance',
    metrics: [
      { key: 'revenue', label: 'Revenue', icon: IndianRupee, fmt: fmtINR, betterHigher: true },
      { key: 'expense', label: 'Expense', icon: Wallet, fmt: fmtINR, betterHigher: false },
      { key: 'profit', label: 'Profit', icon: TrendingUp, fmt: fmtINR, betterHigher: true },
      { key: 'loss', label: 'Loss', icon: TrendingDown, fmt: fmtINR, betterHigher: false },
    ],
  },
  {
    title: 'Patient Footfall',
    metrics: [
      { key: 'patients_visited', label: 'Unique Patients', icon: Users, fmt: fmtNum, betterHigher: true },
      { key: 'opd_visits', label: 'OPD Visits', icon: Activity, fmt: fmtNum, betterHigher: true },
      { key: 'ipd_admissions', label: 'IPD Admissions', icon: Bed, fmt: fmtNum, betterHigher: true },
    ],
  },
  {
    title: 'Capacity Utilisation',
    metrics: [
      { key: 'beds_occupied', label: 'Beds Occupied', icon: BedDouble, fmt: fmtNum, betterHigher: true },
      { key: 'wards_active', label: 'Active Wards', icon: BedDouble, fmt: fmtNum, betterHigher: true },
    ],
  },
  {
    title: 'Staff Presence',
    metrics: [
      { key: 'staff_present_doctors', label: 'Doctors Present', icon: Stethoscope, fmt: fmtNum, betterHigher: true },
      { key: 'staff_present_nurses', label: 'Nurses Present', icon: HeartPulse, fmt: fmtNum, betterHigher: true },
      { key: 'staff_present_billing', label: 'Billing Staff Present', icon: Wallet, fmt: fmtNum, betterHigher: true },
      { key: 'staff_present_total', label: 'Total Staff Present', icon: Users, fmt: fmtNum, betterHigher: true },
    ],
  },
];

const Stats360Page = () => {
  const { hasPermission } = useAuth();
  const [preset, setPreset] = useState('this_week_vs_last_week');
  const [aStart, setAStart] = useState('');
  const [aEnd, setAEnd] = useState('');
  const [bStart, setBStart] = useState('');
  const [bEnd, setBEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const canView = hasPermission ? hasPermission('dashboard.statistics_360') : false;

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    if (preset === 'custom' && (!aStart || !aEnd || !bStart || !bEnd)) return;
    load();
    // eslint-disable-next-line
  }, [canView, preset, aStart, aEnd, bStart, bEnd]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { preset };
      if (preset === 'custom') Object.assign(params, { a_start: aStart, a_end: aEnd, b_start: bStart, b_end: bEnd });
      const res = await finance360API.comparison(params);
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Comparison failed'); }
    finally { setLoading(false); }
  };

  if (!canView) {
    return <div className="p-8 text-slate-500" data-testid="stats360-restricted">You don't have permission to view the Statistics Dashboard.</div>;
  }

  return (
    <div className="space-y-5" data-testid="stats360-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md">
            <GitCompareArrows size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Statistics Dashboard <span className="text-base font-normal text-slate-500">· Hospital 360°</span></h1>
            <p className="text-sm text-slate-500">Compare any two windows side-by-side — see what's growing, what's slipping.</p>
          </div>
        </div>
      </div>

      {/* Preset */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[260px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Comparison preset</p>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="stats360-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRESET_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {preset === 'custom' && (
            <>
              <div><p className="text-[10px] uppercase text-slate-500 mb-1">A · Start</p><Input type="date" value={aStart} onChange={e => setAStart(e.target.value)} data-testid="stats-a-start" /></div>
              <div><p className="text-[10px] uppercase text-slate-500 mb-1">A · End</p><Input type="date" value={aEnd} onChange={e => setAEnd(e.target.value)} data-testid="stats-a-end" /></div>
              <div><p className="text-[10px] uppercase text-slate-500 mb-1">B · Start</p><Input type="date" value={bStart} onChange={e => setBStart(e.target.value)} data-testid="stats-b-start" /></div>
              <div><p className="text-[10px] uppercase text-slate-500 mb-1">B · End</p><Input type="date" value={bEnd} onChange={e => setBEnd(e.target.value)} data-testid="stats-b-end" /></div>
              <Button onClick={load} className="bg-indigo-600 hover:bg-indigo-700">Compare</Button>
            </>
          )}
        </CardContent>
      </Card>

      {loading || !data ? (
        <div className="p-12 text-center text-slate-400 text-sm">Comparing periods…</div>
      ) : (
        <>
          {/* Period banner */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PeriodBanner label="A" color="indigo" data={data.a} />
            <PeriodBanner label="B" color="slate" data={data.b} />
          </div>

          {/* Metric groups */}
          {METRIC_GROUPS.map(group => (
            <Card key={group.title}>
              <CardHeader className="pb-2"><CardTitle className="text-base">{group.title}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.metrics.map(m => <MetricCompareRow key={m.key} metric={m} delta={data.deltas[m.key]} />)}
                </div>
                {/* Visual bar chart for the group */}
                <div className="mt-4">
                  <ResponsiveContainer width="100%" height={Math.max(180, group.metrics.length * 40)}>
                    <BarChart data={group.metrics.map(m => ({
                      name: m.label,
                      A: data.deltas[m.key].a,
                      B: data.deltas[m.key].b,
                    }))} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="A" fill="#6366f1" name={`A · ${data.a.label}`} radius={[0, 4, 4, 0]} />
                      <Bar dataKey="B" fill="#94a3b8" name={`B · ${data.b.label}`} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
};

const PeriodBanner = ({ label, color, data }) => {
  const colorMap = { indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700', slate: 'bg-slate-50 border-slate-200 text-slate-700' };
  return (
    <div className={`rounded-xl border p-3 ${colorMap[color]}`}>
      <p className="text-[10px] uppercase font-bold tracking-wide opacity-70">Period {label}</p>
      <p className="text-base font-bold">{data.label}</p>
      <p className="text-[10px] opacity-60">{new Date(data.range.start).toLocaleDateString('en-IN')} → {new Date(data.range.end).toLocaleDateString('en-IN')} ({data.range.days}d)</p>
    </div>
  );
};

const MetricCompareRow = ({ metric: m, delta }) => {
  const Icon = m.icon;
  const positive = delta.delta_pct > 0;
  const negative = delta.delta_pct < 0;
  const isGood = (positive && m.betterHigher) || (negative && !m.betterHigher);
  const trendColor = delta.delta_pct === 0 ? 'text-slate-400' : isGood ? 'text-emerald-600' : 'text-rose-600';
  const TrendIcon = positive ? TrendingUp : negative ? TrendingDown : Activity;
  return (
    <div className="rounded-lg border border-slate-200 p-3 hover:shadow-sm transition" data-testid={`stats-row-${m.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-600 text-xs font-semibold"><Icon size={14} /> {m.label}</div>
        <span className={`text-[10px] font-bold inline-flex items-center gap-0.5 ${trendColor}`}>
          <TrendIcon size={11} /> {delta.delta_pct > 0 ? '+' : ''}{delta.delta_pct}%
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
        <div className="rounded p-2 bg-indigo-50 border border-indigo-100"><p className="text-[9px] uppercase font-bold text-indigo-700 opacity-70">A</p><p className="font-bold text-indigo-900">{m.fmt(delta.a)}</p></div>
        <div className="rounded p-2 bg-slate-50 border border-slate-100"><p className="text-[9px] uppercase font-bold text-slate-600 opacity-70">B</p><p className="font-bold text-slate-700">{m.fmt(delta.b)}</p></div>
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5">Δ {delta.delta > 0 ? '+' : ''}{m.fmt(delta.delta)}</p>
    </div>
  );
};

export default Stats360Page;
